import { VACCINE_CODE_MAP } from '@constants/option-types/immunization';

import { formatFhirDate, getFhirDate } from '@utils/fhir';
import { formatAddress } from '@utils/format';

import type {
  ImmunizationModel,
  ImmunizationModelInput,
} from 'generated/types';
import { ImmunizationStatus } from 'generated/types';

import { FhirResource, type BaseFhirFormValues } from './fhir-resource';

export type ImmunizationValues = Pick<
  ImmunizationRecord,
  | 'brand'
  | 'city'
  | 'country'
  | 'dose'
  | 'expirationDate'
  | 'facility'
  | 'immunizationDate'
  | 'lotNumber'
  | 'practitioner'
  | 'region'
  | 'route'
  | 'selfReported'
  | 'standard'
> & {
  // vaccineName
  title: string;
  associatedResources?: string[];
};

export type FormValues = BaseFhirFormValues & ImmunizationValues;

export class ImmunizationRecord extends FhirResource {
  protected model: ImmunizationModel;

  private TRAVEL_CODE = '281657000';

  private STANDARD_CODE = '429060002';

  public constructor(model: ImmunizationModel) {
    super(model);
    this.model = model;
  }

  public get recordType() {
    return this.model.__typename;
  }

  public get vaccineName() {
    return this.model.vaccineCode.text;
  }

  public set vaccineName(value: string | null | undefined) {
    if (!value) {
      return;
    }
    this.model = {
      ...this.model,
      vaccineCode: {
        text: value,
        ...(VACCINE_CODE_MAP[value] && {
          coding: [
            {
              code: VACCINE_CODE_MAP[value],
              display: value,
              system: ImmunizationRecord.VITALL_SYSTEM,
            },
          ],
        }),
      },
    };
  }

  public get vaccineCode() {
    return this.model.vaccineCode.coding?.map(({ code }) => code).join(', ');
  }

  public get expirationDate() {
    return this.model.expirationDate;
  }

  public set expirationDate(value: string | null | undefined) {
    this.model = {
      ...this.model,
      expirationDate: value,
    };
  }

  public get immunizationDate() {
    return this.model.occurrenceDateTime ?? this.model.occurrenceString;
  }

  public set immunizationDate(value: string | null | undefined) {
    this.model = {
      ...this.model,
      occurrenceDateTime: value,
      occurrenceString: value ?? 'Unknown',
    };
  }

  public get facility() {
    const location = this.model.locationResource;
    if (!location) {
      return null;
    }
    return location.name;
  }

  public get address() {
    const location = this.model.locationResource;
    if (!location) {
      return null;
    }
    const { address } = location;
    return formatAddress(
      address?.line,
      address?.city,
      address?.postalCode,
      address?.state,
      address?.country
    );
  }

  public get city() {
    return this.model.locationResource?.address?.city;
  }

  public get region() {
    return this.model.locationResource?.address?.state;
  }

  public get country() {
    return this.model.locationResource?.address?.country;
  }

  public get practitioner() {
    return this.model.performer?.map(({ actor }) => actor.display).join('/');
  }

  public set practitioner(value: string | null | undefined) {
    this.model = {
      ...this.model,
      performer: value
        ? [
            {
              actor: { display: value },
            },
          ]
        : null,
    };
  }

  public get dose() {
    return this.model.protocolApplied
      ?.map(({ doseNumberString }) => doseNumberString)
      .join('/');
  }

  public set dose(value: string | null | undefined) {
    this.model = {
      ...this.model,
      protocolApplied: value
        ? [
            {
              doseNumberString: value,
            },
          ]
        : null,
    };
  }

  public get brand() {
    return this.model.manufacturer?.display;
  }

  public set brand(value: string | null | undefined) {
    this.model = {
      ...this.model,
      manufacturer: {
        display: value,
      },
    };
  }

  public get lotNumber() {
    return this.model.lotNumber;
  }

  public set lotNumber(value: string | null | undefined) {
    this.model = {
      ...this.model,
      lotNumber: value,
    };
  }

  public get route() {
    return this.model.route?.text;
  }

  public set route(value: string | null | undefined) {
    this.model = {
      ...this.model,
      route: value ? { text: value } : null,
    };
  }

  public get standard() {
    return (
      this.model.reasonCode?.some(({ coding }) =>
        // SNOMED code for standard
        coding?.some(
          ({ code }) => code === this.STANDARD_CODE || code !== this.TRAVEL_CODE
        )
      ) ?? true
    );
  }

  public set standard(value: boolean | undefined) {
    this.model = {
      ...this.model,
      reasonCode: [
        {
          text: value ? 'Standard' : 'Travel',
          coding: [
            {
              code: value ? this.STANDARD_CODE : this.TRAVEL_CODE,
              display: value ? 'Standard' : 'Travel',
              system: 'http://snomed.info/sct',
            },
          ],
        },
      ],
    };
  }

  public getPatient() {
    return this.model.patient.reference;
  }

  public isTravel() {
    return (
      this.model.reasonCode?.some(({ coding }) =>
        // SNOMED code for travel
        coding?.some(({ code }) => code === this.TRAVEL_CODE)
      ) ?? false
    );
  }

  public isStandard() {
    return (
      this.model.reasonCode?.some(({ coding }) =>
        // SNOMED code for standard
        coding?.some(
          ({ code }) => code === this.STANDARD_CODE || code !== this.TRAVEL_CODE
        )
      ) ?? true
    );
  }

  public getExpiryDate() {
    return getFhirDate(this.model.expirationDate);
  }

  public getExpiryDateStr() {
    return formatFhirDate(this.model.expirationDate, { showTime: false });
  }

  public getImmunizationDate() {
    return getFhirDate(
      this.model.occurrenceDateTime ?? this.model.occurrenceString
    );
  }

  public getImmunizationDateStr() {
    return formatFhirDate(
      this.model.occurrenceDateTime ?? this.model.occurrenceString,
      { showTime: false }
    );
  }

  public getModelInput(values?: ImmunizationValues): ImmunizationModelInput {
    if (values) {
      // eslint-disable-next-line guard-for-in
      for (const key in values) {
        try {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-expect-error
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          this[key] = values[key];
        } catch {
          // eslint-disable-next-line no-continue
          continue;
        }
      }
      this.vaccineName = values.title;
    }
    const cleaned = FhirResource.getCleanInput(this.model);
    delete cleaned.location;
    delete cleaned.locationResource;
    return { ...cleaned, status: ImmunizationStatus.notDone };
  }
}
